/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * My profile › Invitations: waiting for you first, then those you sent.
 */
import React from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@lemonadetraining/web-ui";
import type { Invite } from "@lemonadetraining/contracts";
import InvitationView from "./InvitationView";
import { useDisplayZone } from "../../hooks/useDisplayZone";
import { useOwnGymZone } from "../../hooks/useOwnGymZone";

interface InvitationsSectionProps {
  /** null while loading. */
  received: Invite[] | null;
  sent: Invite[] | null;
  failed: boolean;
  onReload: () => void;
  /** Owners and admins: staff invitations live on Team. */
  showTeamNote: boolean;
}

const pendingFirst = (a: Invite, b: Invite) =>
  Number(b.status === "Pending") - Number(a.status === "Pending");

const InvitationsSection: React.FC<InvitationsSectionProps> = ({
  received,
  sent,
  failed,
  onReload,
  showTeamNote,
}) => {
  // One read for the whole list; each row would otherwise read the gym again.
  const display = useDisplayZone(
    useOwnGymZone((received?.length ?? 0) + (sent?.length ?? 0) > 0).zone
  );
  const zone = display.isLoading ? undefined : display.zone;
  if (failed) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 pt-6 text-sm" role="alert">
          <span className="text-destructive">We couldn&apos;t load your invitations.</span>
          <Button variant="outline" className="min-h-[44px]" onClick={onReload}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (received === null || sent === null) {
    return (
      <div aria-busy="true" data-testid="invitations-loading">
        <span className="sr-only">Loading invitations</span>
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      <Card data-testid="invitations-received">
        <CardHeader>
          <CardTitle>Invitations for you</CardTitle>
          <CardDescription>See what you&apos;re agreeing to before you accept.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {received.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invitations right now.</p>
          ) : (
            [...received]
              .sort(pendingFirst)
              .map((invite) => (
                <InvitationView
                  key={invite.id}
                  direction="received"
                  invite={invite}
                  onActed={onReload}
                  timeZone={zone}
                />
              ))
          )}
        </CardContent>
      </Card>

      {(sent.length > 0 || showTeamNote) && (
        <Card data-testid="invitations-sent">
          <CardHeader>
            <CardTitle>Invitations you sent</CardTitle>
            <CardDescription>
              Programs and 1-to-1 (PT) clients you invited.
              {showTeamNote && " Invitations to your gym's team are on Team."}
            </CardDescription>
          </CardHeader>
          {sent.length > 0 && (
            <CardContent className="flex flex-col gap-3">
              {sent.map((invite) => (
                <InvitationView key={invite.id} direction="sent" invite={invite} timeZone={zone} />
              ))}
            </CardContent>
          )}
        </Card>
      )}
    </>
  );
};

export default InvitationsSection;
